import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { AlertCircle, FileSpreadsheet, History, KeyRound, Loader2, Pencil, Plus, Trash2, Upload, X } from 'lucide-react';
import { Button } from '@components/Button';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { ConfirmDialog } from '@components/Dialog';
import { Dropzone, type PickedFile } from '@components/Dropzone';
import { FileTypeIcon } from '@components/FileTypeIcon';
import { Pagination } from '@components/Pagination';
import { ActionButton } from '@components/Table';
import accountService from '@services/accountService';
import categoryService from '@services/categoryService';
import creditCardService from '@services/creditCardService';
import importService, { validateImportFile } from '@services/importService';
import { getConfig } from '../../config';
import type { Pagination as PaginationInfo } from '@/types/activity';
import {
  IMPORT_BATCH_STATUS_LABELS,
  IMPORT_DATE_FORMATS,
  IMPORT_FILE_FORMAT_LABELS,
  IMPORT_TARGET_TYPE_LABELS,
  fileFormatFromName,
  importAcceptFor,
  type Account,
  type Category,
  type CreditCard,
  type ImportBatch,
  type ImportBatchStatus,
  type ImportLayout,
  type ImportTargetType,
} from '@/types/finance';
import { ApiError } from '@/utils/apiError';
import { formatBytes, formatDateBR, formatDateTimeBR } from '@/utils/formatters';
import { PillTabs, labelCls, Required } from './financeBits';
import { ImportPreview } from './ImportPreview';
import { ImportLayoutFormModal } from './ImportLayoutFormModal';

type Tab = 'import' | 'history' | 'layouts';

const BATCH_BADGE: Record<ImportBatchStatus, string> = { previewing: 'badge-warning', confirmed: 'badge-success', discarded: 'badge-neutral' };

/**
 * Finanças → Importar: (1) alvo (conta ou cartão) + arquivo (OFX/CSV/XLSX/PDF, ≤ 5 MB; PDF = fatura
 * do cartão ou extrato de conta do app do Itaú, com senha opcional) + layout opcional → POST /imports; (2) prévia editável
 * (ImportPreview) → confirmar/descartar; aba Histórico (GET /imports, reabre prévias) e aba Layouts
 * (CRUD com editor por amostra).
 */
export const ImportView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>('import');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [cards, setCards] = useState<CreditCard[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [layouts, setLayouts] = useState<ImportLayout[]>([]);
  const [error, setError] = useState<unknown>(null);

  // Etapa 1 (`?account_id=` ou `?card_id=` pré-selecionam o alvo)
  const [targetType, setTargetType] = useState<ImportTargetType>(() => (Number(searchParams.get('card_id')) > 0 ? 'card' : 'account'));
  const [accountId, setAccountId] = useState<number | ''>(() => (Number(searchParams.get('account_id')) > 0 ? Number(searchParams.get('account_id')) : ''));
  const [cardId, setCardId] = useState<number | ''>(() => (Number(searchParams.get('card_id')) > 0 ? Number(searchParams.get('card_id')) : ''));
  const [file, setFile] = useState<File | null>(null);
  const [fileProblem, setFileProblem] = useState<string | null>(null);
  const [layoutId, setLayoutId] = useState<number | ''>('');
  /** Senha do PDF protegido (faturas Itaú/Nubank: CPF; extrato Itaú raramente). Só enviada quando o arquivo é PDF. */
  const [pdfPassword, setPdfPassword] = useState('');
  const [reading, setReading] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);

  // Etapa 2
  const [batch, setBatch] = useState<ImportBatch | null>(null);
  const [loadingBatch, setLoadingBatch] = useState(false);

  // Histórico
  const [history, setHistory] = useState<ImportBatch[]>([]);
  const [historyStatus, setHistoryStatus] = useState<ImportBatchStatus | ''>('');
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPagination, setHistoryPagination] = useState<PaginationInfo | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Layouts
  const [layoutForm, setLayoutForm] = useState<{ layout?: ImportLayout } | null>(null);
  const [deletingLayout, setDeletingLayout] = useState<ImportLayout | null>(null);
  const [busy, setBusy] = useState(false);

  const pageSize = getConfig().ui.itemsPerPage || 20;

  const loadLayouts = useCallback(() => importService.getLayouts().then(setLayouts).catch(setError), []);

  useEffect(() => {
    accountService.getAll().then(setAccounts).catch(setError);
    creditCardService.getAll().then(setCards).catch(setError);
    categoryService.getAll().then(setCategories).catch(setError);
    loadLayouts();
  }, [loadLayouts]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await importService.list({ status: historyStatus, page: historyPage, page_size: pageSize });
      setHistory(res.data);
      setHistoryPagination(res.pagination);
    } catch (err) {
      setError(err);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyStatus, historyPage, pageSize]);

  useEffect(() => {
    if (tab === 'history') loadHistory();
  }, [tab, loadHistory]);

  const fileFormat = file ? fileFormatFromName(file.name) : null;
  const isPdf = fileFormat === 'pdf';
  const layoutOptions = useMemo(() => layouts.filter((l) => l.target_type === targetType && (!fileFormat || l.file_format === fileFormat)), [layouts, fileFormat, targetType]);
  const targetId = targetType === 'account' ? accountId : cardId;

  const pickFile = (files: PickedFile[], target: ImportTargetType = targetType) => {
    const f = files[0]?.file ?? null;
    setFile(f);
    setFileProblem(f ? validateImportFile(f, target) : null);
    setStepError(null);
    setLayoutId('');
    if (!f || fileFormatFromName(f.name) !== 'pdf') setPdfPassword('');
  };

  const changeTarget = (t: ImportTargetType) => {
    setTargetType(t);
    setLayoutId('');
    setStepError(null);
    // Revalida o arquivo já escolhido ao trocar o alvo (a validação pode depender do alvo).
    if (file) setFileProblem(validateImportFile(file, t));
  };

  const canRead = !reading && targetId !== '' && !!file && !fileProblem;

  const read = async () => {
    if (!canRead || !file) return;
    setReading(true);
    setStepError(null);
    try {
      setBatch(await importService.create(file, targetType, Number(targetId), layoutId || null, isPdf ? pdfPassword.trim() || null : null));
      setTab('import');
    } catch (err) {
      if (err instanceof ApiError && (err.status === 400 || err.status === 409)) setStepError(err.message);
      else setError(err);
    } finally {
      setReading(false);
    }
  };

  const openBatch = async (id: number) => {
    setLoadingBatch(true);
    try {
      setBatch(await importService.getById(id));
      setTab('import');
    } catch (err) {
      setError(err);
    } finally {
      setLoadingBatch(false);
    }
  };

  const resetWizard = () => {
    setBatch(null);
    setFile(null);
    setFileProblem(null);
    setLayoutId('');
    setPdfPassword('');
    setStepError(null);
  };

  const confirmDeleteLayout = async () => {
    if (!deletingLayout) return;
    setBusy(true);
    try {
      await importService.removeLayout(deletingLayout.id);
      setDeletingLayout(null);
      loadLayouts();
    } catch (err) {
      setDeletingLayout(null);
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  // ------------------------------------------------------------------ etapa 1

  const step1 = (
    <div className="card p-4 sm:p-5 space-y-4 max-w-3xl">
      <div>
        <label className={labelCls}>
          Importar para
          <Required />
        </label>
        <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden" role="radiogroup">
          {(['account', 'card'] as ImportTargetType[]).map((t) => (
            <button
              key={t}
              type="button"
              role="radio"
              aria-checked={targetType === t}
              disabled={reading}
              onClick={() => changeTarget(t)}
              className={clsx('px-4 py-2 text-sm font-medium transition-colors', targetType === t ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50')}
            >
              {t === 'account' ? 'Conta (extrato)' : 'Cartão (fatura)'}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {targetType === 'account' ? (
          <div>
            <label className={labelCls}>
              Conta
              <Required />
            </label>
            <select className="select-field" value={accountId} onChange={(e) => setAccountId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">Selecione…</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            {accounts.length === 0 ? (
              <p className="text-small mt-1">Cadastre uma conta em Finanças → Contas antes de importar.</p>
            ) : (
              <p className="text-small mt-1">
                Aceita também o <strong>PDF do extrato</strong> exportado pelo app do Itaú: lê os lançamentos e o saldo em conta, que pode ajustar o saldo da conta ao confirmar.
              </p>
            )}
          </div>
        ) : (
          <div>
            <label className={labelCls}>
              Cartão
              <Required />
            </label>
            <select className="select-field" value={cardId} onChange={(e) => setCardId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">Selecione…</option>
              {cards.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.last4 ? ` · final ${c.last4}` : ''}
                </option>
              ))}
            </select>
            {cards.length === 0 ? (
              <p className="text-small mt-1">Cadastre um cartão em Finanças → Cartões antes de importar.</p>
            ) : (
              <p className="text-small mt-1">Aceita também o <strong>PDF da fatura</strong>. Cada linha cai na fatura da competência da data (pelo dia de fechamento); dá para trocar na prévia.</p>
            )}
          </div>
        )}
        <div>
          <label className={labelCls}>Layout (CSV/XLSX)</label>
          <select className="select-field" value={layoutId} disabled={fileFormat === 'ofx' || isPdf} onChange={(e) => setLayoutId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">{fileFormat === 'ofx' ? 'OFX não precisa de layout' : isPdf ? 'PDF não usa layout' : 'Automático (reconhecer pelo cabeçalho)'}</option>
            {layoutOptions.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name} ({IMPORT_FILE_FORMAT_LABELS[l.file_format]})
              </option>
            ))}
          </select>
          <p className="text-small mt-1">
            Sem layout, o sistema tenta reconhecer as colunas data / descrição / valor. Se não conseguir, crie um na aba Layouts
            {targetType === 'card' ? ' (fatura costuma trazer a despesa positiva: marque "inverter o sinal")' : ''}.
          </p>
        </div>
      </div>

      <div>
        <label className={labelCls}>
          Arquivo
          <Required />
        </label>
        {file ? (
          <div className={clsx('rounded-lg border p-3 flex items-center gap-3', fileProblem ? 'border-red-200 bg-red-50/40' : 'border-slate-200')}>
            <FileTypeIcon fileName={file.name} contentType={file.type} />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-slate-800 truncate">{file.name}</p>
              <p className="text-small">
                {formatBytes(file.size)}
                {fileFormat && ` · ${IMPORT_FILE_FORMAT_LABELS[fileFormat]}`}
              </p>
              {fileProblem && (
                <p className="text-xs text-red-600 inline-flex items-center gap-1 mt-1">
                  <AlertCircle size={12} /> {fileProblem}
                </p>
              )}
            </div>
            <button type="button" className="p-1 text-slate-400 hover:text-red-600" title="Remover" onClick={() => pickFile([])} disabled={reading}>
              <X size={16} />
            </button>
          </div>
        ) : (
          <Dropzone
            onFiles={(files) => pickFile(files)}
            accept={importAcceptFor(targetType)}
            multiple={false}
            disabled={reading}
            title={targetType === 'card' ? 'Arraste a fatura aqui ou clique para escolher' : 'Arraste o extrato aqui ou clique para escolher'}
            hint={
              targetType === 'card'
                ? 'OFX, CSV, XLSX ou PDF da fatura — até 5 MB. Nada é gravado até você confirmar a prévia.'
                : 'OFX, CSV, XLSX ou PDF do extrato (app do Itaú) — até 5 MB. Nada é gravado até você confirmar a prévia.'
            }
          />
        )}
      </div>

      {isPdf && !fileProblem && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>
              <span className="inline-flex items-center gap-1">
                <KeyRound size={13} /> Senha do PDF (se protegido)
              </span>
            </label>
            <input
              type="password"
              className="input-field"
              autoComplete="off"
              value={pdfPassword}
              disabled={reading}
              onChange={(e) => setPdfPassword(e.target.value)}
              placeholder="deixe em branco se o PDF abre sem senha"
            />
            <p className="text-small mt-1">
              {targetType === 'card'
                ? 'Faturas do Itaú e do Nubank costumam vir protegidas com o CPF do titular (só números). A senha não é guardada.'
                : 'O extrato do app do Itaú normalmente abre sem senha; se o banco proteger, informe aqui (a senha não é guardada).'}
            </p>
          </div>
          <p className="text-small rounded-lg bg-slate-50 border border-slate-200 p-2 self-end">
            {targetType === 'card' ? (
              <>
                Leitura de PDF é <strong>melhor esforço</strong>: funciona com a fatura completa em PDF do banco (texto). PDF digitalizado/foto não é lido — nesse caso exporte OFX ou CSV.
              </>
            ) : (
              <>
                Leitura de PDF é <strong>melhor esforço</strong>. Extrato em PDF do app do Itaú: lê os lançamentos e o saldo em conta do extrato. PDF digitalizado/foto não é lido — nesse caso exporte OFX ou CSV.
              </>
            )}
          </p>
        </div>
      )}

      {stepError && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex gap-2">
          <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
          <span>{stepError}</span>
        </div>
      )}

      <div className="flex justify-end">
        <Button variant="primary" icon={<Upload />} onClick={read} loading={reading} disabled={!canRead}>
          Ler arquivo e ver a prévia
        </Button>
      </div>
    </div>
  );

  // ------------------------------------------------------------------ histórico

  const historyPanel = (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:flex items-center gap-2">
        <select
          className="select-field sm:w-56"
          value={historyStatus}
          onChange={(e) => {
            setHistoryStatus(e.target.value as ImportBatchStatus | '');
            setHistoryPage(1);
          }}
        >
          <option value="">Todas as situações</option>
          <option value="previewing">Em prévia</option>
          <option value="confirmed">Importadas</option>
          <option value="discarded">Descartadas</option>
        </select>
      </div>

      <div className="md:hidden space-y-2">
        {historyLoading && history.length === 0 && (
          <p className="text-center text-slate-400 py-6">
            <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
          </p>
        )}
        {!historyLoading && history.length === 0 && <p className="text-center text-slate-500 py-8">Nenhuma importação.</p>}
        {history.map((b) => (
          <div key={b.id} className={clsx('card p-3 space-y-1 cursor-pointer', historyLoading && 'opacity-60')} onClick={() => openBatch(b.id)}>
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-medium text-slate-800 [overflow-wrap:anywhere]">{b.file_name}</p>
              <span className={BATCH_BADGE[b.status]}>{IMPORT_BATCH_STATUS_LABELS[b.status]}</span>
            </div>
            <p className="text-small">
              {IMPORT_TARGET_TYPE_LABELS[b.target_type]} {b.target_name} · {formatDateTimeBR(b.created_at)} · {b.line_count} linhas, {b.imported_count} importadas
            </p>
          </div>
        ))}
        {historyPagination && <Pagination page={historyPagination.page} totalPages={historyPagination.total_pages} totalItems={historyPagination.total_items} onChange={setHistoryPage} />}
      </div>

      <div className="table-container bg-white hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="table-header">
              <tr>
                <th>Arquivo</th>
                <th>Destino</th>
                <th>Lido em</th>
                <th>Período</th>
                <th>Situação</th>
                <th className="text-right">Linhas / importadas</th>
              </tr>
            </thead>
            <tbody>
              {historyLoading && history.length === 0 && (
                <tr>
                  <td colSpan={6} className="table-cell text-center text-slate-400">
                    <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
                  </td>
                </tr>
              )}
              {!historyLoading && history.length === 0 && (
                <tr>
                  <td colSpan={6} className="table-cell text-center py-12 text-slate-500">
                    Nenhuma importação.
                  </td>
                </tr>
              )}
              {history.map((b) => (
                <tr key={b.id} className={clsx('table-row cursor-pointer', historyLoading && 'opacity-60')} onClick={() => openBatch(b.id)}>
                  <td className="table-cell">
                    <p className="font-medium text-slate-800 [overflow-wrap:anywhere]">{b.file_name}</p>
                    <p className="text-small">{IMPORT_FILE_FORMAT_LABELS[b.file_format]}</p>
                  </td>
                  <td className="table-cell">
                    {b.target_name}
                    <span className="block text-[11px] text-slate-400">{IMPORT_TARGET_TYPE_LABELS[b.target_type]}</span>
                  </td>
                  <td className="table-cell whitespace-nowrap">{formatDateTimeBR(b.created_at)}</td>
                  <td className="table-cell whitespace-nowrap">{b.period_start && b.period_end ? `${formatDateBR(b.period_start)} a ${formatDateBR(b.period_end)}` : <span className="text-slate-400">—</span>}</td>
                  <td className="table-cell">
                    <span className={BATCH_BADGE[b.status]}>{IMPORT_BATCH_STATUS_LABELS[b.status]}</span>
                  </td>
                  <td className="table-cell text-right tabular-nums">
                    {b.line_count} / {b.imported_count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {historyPagination && <Pagination page={historyPagination.page} totalPages={historyPagination.total_pages} totalItems={historyPagination.total_items} onChange={setHistoryPage} />}
      </div>
    </div>
  );

  // ------------------------------------------------------------------ layouts

  const layoutsPanel = (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-small max-w-2xl">
          Layouts dizem ao sistema em quais colunas do seu CSV/XLSX estão a data, a descrição e o valor, para conta (extrato) ou cartão (fatura). OFX não
          precisa de layout. Use um arquivo de exemplo do banco para montar o layout apontando as colunas.
        </p>
        <Button variant="primary" size="sm" icon={<Plus />} onClick={() => setLayoutForm({})}>
          Novo layout
        </Button>
      </div>
      {layouts.length === 0 ? (
        <p className="text-center text-slate-500 py-8">Nenhum layout. Só é necessário quando a importação não reconhece as colunas do arquivo.</p>
      ) : (
        <ul className="card divide-y divide-slate-100 overflow-hidden">
          {layouts.map((l) => (
            <li key={l.id} className="px-3 sm:px-4 py-3 flex items-center gap-3">
              <FileSpreadsheet size={18} className="text-slate-400 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-800 truncate">{l.name}</p>
                <p className="text-small">
                  {IMPORT_TARGET_TYPE_LABELS[l.target_type]} · {IMPORT_FILE_FORMAT_LABELS[l.file_format]}
                  {l.file_format === 'csv' && l.delimiter ? ` (${l.delimiter === '\t' ? 'tab' : l.delimiter})` : ''} · data col. {l.date_column}, descrição col.{' '}
                  {l.description_column}, valor col. {l.amount_column}
                  {l.credit_column !== null && l.credit_column !== undefined ? `, crédito col. ${l.credit_column}` : ''} ·{' '}
                  {IMPORT_DATE_FORMATS.find((d) => d.value === l.date_format)?.label ?? l.date_format}
                  {l.has_header ? ' · com cabeçalho' : ''}
                  {l.skip_rows > 0 ? ` · pula ${l.skip_rows}` : ''}
                  {l.invert_sign ? ' · sinal invertido' : ''}
                </p>
              </div>
              <div className="flex items-center">
                <ActionButton icon={<Pencil size={16} />} variant="edit" tooltip="Editar" onClick={() => setLayoutForm({ layout: l })} />
                <ActionButton icon={<Trash2 size={16} />} variant="delete" tooltip="Excluir" onClick={() => setDeletingLayout(l)} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-h3 flex items-center gap-2">
          <Upload size={20} className="text-slate-500" /> Importar extrato ou fatura
        </h1>
        {tab === 'import' && batch && (
          <Button variant="outline" size="sm" onClick={resetWizard}>
            Novo arquivo
          </Button>
        )}
      </div>

      <PillTabs
        tabs={[
          { id: 'import', label: 'Importar', icon: <Upload size={15} /> },
          { id: 'history', label: 'Histórico', icon: <History size={15} /> },
          { id: 'layouts', label: 'Layouts', icon: <FileSpreadsheet size={15} />, count: layouts.length },
        ]}
        active={tab}
        onChange={(id) => setTab(id as Tab)}
      />

      {tab === 'import' &&
        (loadingBatch ? (
          <p className="text-center text-slate-400 py-6">
            <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando a prévia…
          </p>
        ) : batch ? (
          <ImportPreview batch={batch} categories={categories} onBatchChange={setBatch} onDone={resetWizard} onError={setError} />
        ) : (
          step1
        ))}
      {tab === 'history' && historyPanel}
      {tab === 'layouts' && layoutsPanel}

      {layoutForm && (
        <ImportLayoutFormModal
          layout={layoutForm.layout}
          onClose={() => setLayoutForm(null)}
          onSaved={() => {
            setLayoutForm(null);
            loadLayouts();
          }}
          onError={setError}
        />
      )}

      <ConfirmDialog
        isOpen={!!deletingLayout}
        onClose={() => setDeletingLayout(null)}
        onConfirm={confirmDeleteLayout}
        title="Excluir layout"
        message={`Excluir o layout "${deletingLayout?.name ?? ''}"? As importações já feitas não mudam.`}
        confirmText="Excluir"
        severity="danger"
        loading={busy}
      />

      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
